import * as React from "react";
import { useCallback, useMemo } from "react";
import styled, { css } from "styled-components";
import lowerCase from "lodash/lowerCase";
import { components } from "react-select";
import PaginatedSelect from "components/common/Select/PaginatedSelect";
import { fetchPaginatedCollections } from "collections/hooks/useCollections";
import { Input } from "components/bootstrap";
import { createGRN } from "logic/permissions/GRN";
import { Spinner, Tooltip } from "components/common";
import useCurrentUser from "hooks/useCurrentUser";
import useUserDefaultCollectionsOptions from "collections/hooks/useUserDefaultCollectionsOptions";
import { COLLECTION_EXCLUDED_ENTITY_TYPES } from "collections/constants";
export const StyledLabel = styled.span(
  ({ theme }) => css`
    font-size: ${theme.fonts.size.h5};
  `
);
const MultiValueLabel = (props) => /* @__PURE__ */ React.createElement(Tooltip, { label: "This collection is assigned from teams membership", disabled: !props.data.isFixed }, /* @__PURE__ */ React.createElement("span", null, /* @__PURE__ */ React.createElement(components.MultiValueLabel, { ...props })));
const MultiValueRemove = (props) => {
  var _a;
  if ((_a = props.data) == null ? void 0 : _a.isFixed) {
    return null;
  }
  return /* @__PURE__ */ React.createElement(components.MultiValueRemove, { ...props });
};
const formatCollections = (collections) => collections == null ? void 0 : collections.map((collection) => ({
  label: `${collection.title}`,
  value: createGRN("collection", collection.id),
  isDefault: false
}));
const defaultSearchOptions = {
  page: 1,
  pageSize: 10,
  query: "",
  sort: {
    attributeId: "title",
    direction: "asc"
  }
};
const AddCollectionFormGroup = ({
  error = null,
  label = "Add to collections",
  entityType = "entity",
  name = "collections",
  hideHelp = false,
  filterValueFromOption = false,
  overrideExclude = false,
  value = [],
  onChange
}) => {
  const user = useCurrentUser();
  const { defaultCollections, removeDefaultCollections, defaultCollectionValue, isLoadingDefaultCollections } = useUserDefaultCollectionsOptions(user.id);
  const filterCollections = useCallback(
    (collectionOptions) => filterValueFromOption ? collectionOptions.filter((c) => !value.includes(c.value)) : collectionOptions,
    [filterValueFromOption, value]
  );
  const loadCollections = useCallback(
    (pagination) => fetchPaginatedCollections(
      {
        ...defaultSearchOptions,
        page: pagination.page,
        pageSize: pagination.perPage,
        query: pagination.query
      },
      true
    ).then((results) => ({
      total: results.pagination.total,
      list: [
        ...filterCollections(removeDefaultCollections(formatCollections(results.list))),
        ...defaultCollections
      ],
      pagination
    })),
    [filterCollections, defaultCollections, removeDefaultCollections]
  );
  const handleChange = (values, actionMeta) => {
    var _a;
    switch (actionMeta.action) {
      case "remove-value":
      case "pop-value":
        if (actionMeta.removedValue.isFixed) {
          return;
        }
        break;
      default:
        break;
    }
    onChange({ selected_collections: (_a = values.split(",")) != null ? _a : [] });
  };
  const defaultValue = useMemo(
    () => !filterValueFromOption ? [...value, ...defaultCollectionValue].join(",") : "",
    [filterValueFromOption, defaultCollectionValue, value]
  );
  if (COLLECTION_EXCLUDED_ENTITY_TYPES.includes(entityType) && !overrideExclude) return null;
  if (isLoadingDefaultCollections) {
    return /* @__PURE__ */ React.createElement(Spinner, null);
  }
  return /* @__PURE__ */ React.createElement(
    Input,
    {
      label: /* @__PURE__ */ React.createElement(StyledLabel, null, label),
      help: !hideHelp ? `Select collections to which this ${lowerCase(entityType)} should be added.` : null,
      formGroupClassName: "",
      id: name,
      error: error || void 0
    },
    /* @__PURE__ */ React.createElement(
      PaginatedSelect,
      {
        id: "collections-select-list",
        value: defaultValue,
        placeholder: "Select collections(s)...",
        onLoadOptions: loadCollections,
        components: { MultiValueLabel, MultiValueRemove },
        multi: true,
        clearable: false,
        onChange: handleChange
      }
    )
  );
};
export default AddCollectionFormGroup;
